import { gql } from '@apollo/client';
import {
  AGENT_RED_MINUTES,
  AGENT_YELLOW_MINUTES,
  ATLAS_CAP,
  ATLAS_CRIT,
  atlasLevel,
  capacityFor,
  type AtlasEntryState,
  type AtlasLevel,
} from '@infras/shared-types';
import { asTargetKind, type TargetKind } from './targetKind';

/**
 * Shared by the three Atlas screens — index, project, mappings.
 *
 * Thresholds are NOT redefined here: they come from shared-types, the same module
 * the resolver imports. A capacity bar that turns amber at a different number than
 * the API's alert is not a styling bug, it is two different claims about whether
 * production is about to lose database access.
 */

/* ATLAS_WARN is deliberately NOT re-exported: `capacityFor(kind)` now hands every
 * caller warn/crit per kind, and the last consumer of the fixed Atlas number went with
 * the per-kind capacity bar (review F-9). Re-adding it would give a do_db screen a way
 * to reach for Atlas's 180 by accident. */
export { ATLAS_CAP, ATLAS_CRIT, AGENT_RED_MINUTES, AGENT_YELLOW_MINUTES, atlasLevel };
export type { AtlasEntryState, AtlasLevel };

// ── the wire ─────────────────────────────────────────────────────────────────

export interface AtlasProjectOverview {
  projectKey: string;
  /** atlas: Atlas project id · do_db: DO database uuid (contract §1 dual meaning). */
  projectId: string;
  /** `atlas` | `do_db` — decides the cap, the badge, and whether prune renders. */
  kind: TargetKind;
  /** do_db only: the `do_accounts.slug` whose token the target holds. */
  doTeam?: string | null;
  displayName?: string | null;
  isActive: boolean;
  hasCredentials: boolean;
  liveCount?: number | null;
  pendingCount?: number | null;
  liveCountAt?: string | null;
  reportedTotal: number;
  clusterCount: number;
  agentsTotal: number;
  /* `agentsStale` is DELIBERATELY NOT SELECTED. It is the server's count of agents
   * silent past its own 30-minute parameter — a second threshold nothing in this app
   * renders any more, since the column that used it was removed with the multi-clock
   * model. Selecting a number we would have to explain away is how the third clock got
   * on screen the first time. */
  lastSyncAt?: string | null;
  lastSyncStatus?: string | null;
}

export interface AtlasEntryRow {
  cidr: string;
  comment?: string | null;
  state: AtlasEntryState;
  clusterName?: string | null;
  nodeName?: string | null;
  lastSeenAt?: string | null;
}

export interface AtlasHeartbeat {
  clusterName: string;
  agentCount: number;
  lastSeenAt: string;
}

export interface UnmappedCluster {
  clusterName: string;
  agentCount: number;
  ignored: boolean;
  ignoredReason?: string | null;
  ignoredBy?: string | null;
}

export const PROJECTS_OVERVIEW = gql`
  query AtlasProjectsOverview {
    atlasProjectsOverview {
      projectKey
      projectId
      kind
      doTeam
      displayName
      isActive
      hasCredentials
      liveCount
      pendingCount
      liveCountAt
      reportedTotal
      clusterCount
      agentsTotal
      lastSyncAt
      lastSyncStatus
    }
  }
`;

/** A scanned DO database, as the Add-target dropdown needs it (contract §9). Empty
 *  means the `do_db_firewall` scan has never run — the form says so, see
 *  `DO_SCAN_HINT`. */
export interface DoDatabaseOption {
  uuid: string;
  name: string;
  engine?: string | null;
  doTeam: string;
}

export const DO_DATABASES = gql`
  query DoDatabases {
    doDatabases {
      uuid
      name
      engine
      doTeam
    }
  }
`;

/**
 * The ONE row behind a single target's engine icon. The list query above answers the
 * same question for a screen that renders many targets (the band, the mappings page);
 * a drawer needs exactly one, and the list resolver runs a per-row firewall-rule count
 * subquery over the whole scanned estate to get there. Null when nobody has scanned
 * that uuid — not an error.
 */
export const DO_DATABASE = gql`
  query DoDatabase($uuid: String!) {
    doDatabase(uuid: $uuid) {
      uuid
      name
      engine
      doTeam
    }
  }
`;

export const ATLAS_ENTRIES = gql`
  query AtlasEntries($projectKey: String!) {
    atlasEntries(projectKey: $projectKey) {
      cidr
      comment
      state
      clusterName
      nodeName
      lastSeenAt
    }
  }
`;

export const ATLAS_PENDING = gql`
  query AtlasPendingIps($projectKey: String!) {
    atlasPendingIps(projectKey: $projectKey) {
      id
      ipCidr
      clusterName
      nodeName
      lastSeenAt
    }
  }
`;

/**
 * Addresses a human prune retired from the desired set.
 *
 * Separate from ATLAS_PENDING on purpose: these are exactly the rows that are NOT waiting
 * to push, and mixing them into that tab would put "will be added" and "was deliberately
 * removed" under one heading.
 */
export const ATLAS_SUPPRESSED = gql`
  query AtlasSuppressedIps($projectKey: String!) {
    atlasSuppressedIps(projectKey: $projectKey) {
      id
      ipCidr
      clusterName
      nodeName
      prunedAt
      prunedBy
      prunedReason
    }
  }
`;

/**
 * Undo a suppression. Root only, and the only repair path there is — reconcile no longer
 * re-adds a suppressed address (that is the point of it), and a healthy agent never
 * re-registers, so nothing else will bring it back.
 */
export const RESTORE_SUPPRESSED = gql`
  mutation RestoreAtlasSuppressedIp($projectKey: String!, $ipCidr: String!) {
    restoreAtlasSuppressedIp(projectKey: $projectKey, ipCidr: $ipCidr) {
      id
      ipCidr
    }
  }
`;

export const ATLAS_HEARTBEATS = gql`
  query AtlasAgentHeartbeats($projectKey: String) {
    atlasAgentHeartbeats(projectKey: $projectKey) {
      clusterName
      agentCount
      lastSeenAt
    }
  }
`;

export const ATLAS_UNMAPPED = gql`
  query AtlasUnmappedClusters {
    atlasUnmappedClusters {
      clusterName
      agentCount
      ignored
      ignoredReason
      ignoredBy
    }
  }
`;

export const SET_IGNORED = gql`
  mutation SetClusterAtlasIgnored($clusterName: String!, $ignored: Boolean!, $reason: String) {
    setClusterAtlasIgnored(clusterName: $clusterName, ignored: $ignored, reason: $reason)
  }
`;

export const REFRESH_LIVE_COUNT = gql`
  mutation RefreshAtlasLiveCount($projectKey: String!) {
    refreshAtlasLiveCount(projectKey: $projectKey) {
      projectKey
      liveCount
      pendingCount
      liveCountAt
    }
  }
`;

/**
 * Remove confirmed entries from a project's live Atlas access list.
 *
 * Both kinds, since e-32: for a do_db target the same mutation removes the address from
 * the DO firewall (fresh GET → PUT without it → GET verify) and suppresses the reported
 * row so Sync now cannot re-add it. The wire shape is identical.
 *
 * `confirmIps` is an allow-list, not a filter: the server re-classifies every
 * address against a FRESH read of the vendor and refuses anything that is no longer
 * reclaimable, reporting it in `refused` rather than removing it. That is why the
 * UI does not need a separate `dryRun` round-trip — the table on screen is already
 * the same classification, and the execution-time re-check is the authority.
 *
 * Three outcomes, and they must stay three: `removed` landed, `refused` is OUR
 * verdict that the entry should stay, `failed` is a delete Atlas rejected and the
 * operator has to retry. Folding `failed` into `refused` would report an address as
 * deliberately kept when in fact the call broke.
 */
export const PRUNE_ORPHANS = gql`
  mutation PruneAtlasOrphans($projectKey: String!, $confirmIps: [String!]) {
    pruneAtlasOrphans(projectKey: $projectKey, dryRun: false, confirmIps: $confirmIps) {
      projectKey
      liveTotal
      removed
      refused {
        cidr
        reason
      }
      failed {
        cidr
        reason
      }
    }
  }
`;

// ── presentation ─────────────────────────────────────────────────────────────

/** Level → the CSS var that colours the number, bar and headline together. */
export function levelColor(level: AtlasLevel | null): string {
  if (level === 'crit') return 'var(--bad)';
  if (level === 'warn') return 'var(--amber)';
  if (level === 'ok') return 'var(--green)';
  // Unknown usage. Muted, never green: "we have not looked" must not read as "fine".
  return 'var(--faint)';
}

export const STATE_LABEL: Record<AtlasEntryState, string> = {
  in_sync: 'in sync',
  no_agent: 'no agent',
  stale: 'stale',
  manual: 'manual',
  open_to_all: 'open to all',
};

/** What each state means, on hover, for a target of `kind`. The words alone don't
 *  carry the consequence — and on a DO firewall the vendor and the cap are different
 *  numbers, so the sentence is built per kind rather than hard-wired to Atlas. */
export function stateTitles(kind: TargetKind | string | null | undefined = 'atlas'): Record<AtlasEntryState, string> {
  const k = asTargetKind(kind);
  const vendor = k === 'do_db' ? 'the DO firewall' : 'Atlas';
  const { cap } = capacityFor(k);
  return {
    in_sync: `In ${vendor}, and an agent reports it.`,
    no_agent: `In ${vendor}, but nothing reports it — it occupies one of the ${cap} slots for nothing.`,
    // AC-14: the word "not recently" used to leave the reader to guess which threshold
    // produced the verdict. There is now only one, and it says which and what follows.
    stale: `An agent reported this address, but has sent no heartbeat in the last ${AGENT_RED_MINUTES} min, which is why this state is reclaimable.`,
    manual:
      k === 'do_db'
        ? 'A non-IP rule (k8s, droplet, tag, app) or an address added in the DO console, outside this system. Never counted as reclaimable, never touched.'
        : 'Added by hand in Atlas, outside this system. Never counted as reclaimable.',
    open_to_all: 'This CIDR admits every address on the internet, which makes the rest of the list decorative.',
  };
}

/** The Atlas wording — what every pre-052 caller reads. */
export const STATE_TITLE: Record<AtlasEntryState, string> = stateTitles('atlas');

/**
 * Capacity arithmetic, in one place — per KIND.
 *
 * `projected` is what the NEXT reconcile would leave behind, and `rejected` is the
 * case that must shout: past the cap Atlas rejects the add outright rather than
 * truncating, so nodes silently lose database access. A DO firewall is the same
 * shape at a different number — 100 rules (e-10: show `n / 100`, surface the cap like
 * Atlas) — and the numbers come from `capacityFor(kind)` / `atlasLevel(live, kind)` in
 * shared-types, the module the resolver reads, never from here. With `kind` omitted
 * every Atlas caller computes exactly what it did before 052.
 */
export function capacity(
  live: number | null | undefined,
  pending: number | null | undefined,
  kind: TargetKind | string | null | undefined = 'atlas',
) {
  const k = asTargetKind(kind);
  const { cap, warn, crit } = capacityFor(k);
  const known = live != null;
  const l = live ?? 0;
  const p = pending ?? 0;
  const projected = l + p;
  const level = atlasLevel(live ?? null, k);
  return {
    kind: k,
    cap,
    warn,
    crit,
    known,
    live: l,
    pending: p,
    projected,
    free: Math.max(0, cap - projected),
    slotsLeft: Math.max(0, cap - l),
    rejected: projected > cap,
    level,
    /** Percentage widths for the two-segment bar. Always over the kind's cap — never
     *  normalised to the biggest project, or the cap stops meaning anything. */
    livePct: known ? Math.min(100, (l / cap) * 100) : 0,
    pendingPct: known ? Math.min(100 - (l / cap) * 100, (p / cap) * 100) : 0,
  };
}

/** Minutes since a timestamp, for the heartbeat colouring. */
export function minutesSince(iso?: string | null): number | null {
  if (!iso) return null;
  return (Date.now() - new Date(iso).getTime()) / 60000;
}

/**
 * Heartbeat severity — THE SAME TWO THRESHOLDS AS EVERY OTHER SURFACE.
 *
 * This dot used to run its own 15/30-minute bands (`ATLAS_AGENT_FRESH_MIN` and
 * `ATLAS_AGENT_STALE_MIN`), which made it a third clock a human could see: one cluster
 * could read "3 silent" here and a different verdict on the fleet page at the same
 * moment, and the card needed a paragraph explaining that it meant something else.
 * Removed 2026-08-25 with the rest of the multi-clock model — amber past
 * AGENT_YELLOW_MINUTES, red past AGENT_RED_MINUTES, no exceptions and nothing to
 * explain.
 */
export function heartbeatColor(mins: number | null): string {
  if (mins == null) return 'var(--faint)';
  if (mins > AGENT_RED_MINUTES) return 'var(--bad)';
  if (mins > AGENT_YELLOW_MINUTES) return 'var(--amber)';
  return 'var(--green)';
}
